import axios from 'axios'
import { Card, type Set, CollectionItem, CollectionStats } from '@/types'
import { OrderType, CreateOrderData } from '@/types/order'
import type {
  BinderSummary,
  BinderDetailResponse,
  UpdateBinderResponse,
  CreateBinderInput,
  UpdateBinderInput,
  BinderSlotInput,
  BinderSlotView,
  BulkAddInput,
  BulkAddResponse,
} from '@/types/binder'
import type {
  BulkboxSummary,
  BulkboxDetailResponse,
  BulkboxGroupsResponse,
  CreateBulkboxInput,
  UpdateBulkboxInput,
  AddGroupInput,
  ReplaceGroupsInput,
} from '@/types/bulkbox'
import type { IntakeItem, BreakProduct } from '@/types/inventory'
import type {
  StoreListing,
  StoreSettingsExtended,
  PricingDefaults,
  BulkPriceRequest,
  BulkPriceResponse,
  PriceSuggestion,
  StockBreakdown,
  Reservation,
  ListingCondition,
  ListingStatus,
  ListingType,
  StockCardsResponse,
  ListingsListResponse,
  PublicStoreCartItem,
} from '@/types/store'
import type {
  PosSession,
  PosSessionSummary,
  PosLookupResult,
  PosCheckoutRequest,
  PosCheckoutResponse,
  PosTransaction,
  PosTransactionSummary,
  PosPaymentMethod,
  PosRefund,
} from '@/types/pos'

// Get the API URL from environment or use default
// In production, if the frontend is served from the same server, use relative URLs
// Otherwise use the configured VITE_API_URL
const API_URL = import.meta.env.VITE_API_URL || ''

// Log the API URL for debugging (remove in production)
if (import.meta.env.DEV) {
  console.log('API URL:', API_URL || 'Using relative URLs')
}

export const api = axios.create({
  baseURL: API_URL,
  withCredentials: true, // Include cookies in requests
})

// Language filter interceptor: appends ?languages=en,ja to card/set list GET requests
const LANGUAGE_FILTER_PATHS = new Set(['/api/sets', '/api/cards', '/api/cards/search'])
api.interceptors.request.use((config) => {
  if (config.method === 'get' && config.url && LANGUAGE_FILTER_PATHS.has(config.url)) {
    const stored = localStorage.getItem('enabled_languages')
    if (stored) {
      try {
        const langs: string[] = JSON.parse(stored)
        if (langs.length > 0) {
          config.params = { ...config.params, languages: langs.join(',') }
        }
      } catch {}
    }
  }
  return config
})

// Helper to update the stored language filter (called from settings/preferences)
export function setEnabledLanguages(languages: string[]) {
  localStorage.setItem('enabled_languages', JSON.stringify(languages))
}

export function getEnabledLanguages(): string[] {
  try {
    const stored = localStorage.getItem('enabled_languages')
    if (stored) return JSON.parse(stored)
  } catch {}
  return ['en']
}

// Add request interceptor to log requests in dev mode
if (import.meta.env.DEV) {
  api.interceptors.request.use(
    (config) => {
      console.log('API Request:', config.method?.toUpperCase(), config.url)
      console.log('Request headers:', config.headers)
      return config
    },
    (error) => {
      console.error('Request error:', error)
      return Promise.reject(error)
    }
  )

  api.interceptors.response.use(
    (response) => {
      console.log('API Response:', response.status, response.config.url)
      return response
    },
    (error) => {
      console.error('API Error:', error.response?.status, error.response?.config?.url, error.response?.data)
      return Promise.reject(error)
    }
  )
}

export const setsApi = {
  getAll: async () => {
    const { data } = await api.get<Set[]>('/api/sets')
    return data
  },
  getById: async (id: string) => {
    const { data } = await api.get<Set>(`/api/sets/${id}`)
    return data
  },
  getCards: async (setId: string) => {
    const { data } = await api.get<Card[]>(`/api/sets/${setId}/cards`)
    return data
  },
  getRarityStats: async () => {
    const { data } = await api.get<Record<string, Record<string, number>>>('/api/sets/rarity-stats')
    return data
  },
}

export const cardsApi = {
  getAll: async (params?: {
    page?: number
    limit?: number
    search?: string
    set?: string
    rarity?: string
    type?: string
  }) => {
    const { data } = await api.get('/api/cards', { params })
    return data
  },
  getById: async (id: string) => {
    const { data } = await api.get<Card>(`/api/cards/${id}`)
    return data
  },
  updateCardmarketUrl: async (cardId: string, url: string) => {
    const { data } = await api.put(`/api/cards/${cardId}/cardmarket-url`, { url })
    return data
  },
  getCardmarketPrices: async (): Promise<Record<string, { min: number; avg: number; trend: number }>> => {
    const { data } = await api.get('/api/cards/cardmarket-prices')
    return data
  },
}

export const ordersApi = {
  getAll: async () => {
    const { data } = await api.get('/api/orders')
    return data
  },

  getByType: async (type: OrderType) => {
    const { data } = await api.get('/api/orders', { params: { type } })
    return data
  },

  getById: async (id: string) => {
    const { data } = await api.get(`/api/orders/${id}`)
    return data
  },

  create: async (orderData: CreateOrderData) => {
    const { data } = await api.post('/api/orders', orderData)
    return data
  },

  updateStatus: async (id: string, status: string) => {
    const { data } = await api.patch(`/api/orders/${id}/status`, { status })
    return data
  },

  update: async (id: string, orderData: Partial<CreateOrderData>) => {
    const { data } = await api.put(`/api/orders/${id}`, orderData)
    return data
  },

  delete: async (id: string) => {
    await api.delete(`/api/orders/${id}`)
  },

  deductStock: async (id: string) => {
    const { data } = await api.post(`/api/orders/${id}/deduct-stock`)
    return data
  },
  reDeductStock: async (id: string) => {
    const { data } = await api.post(`/api/orders/${id}/re-deduct-stock`)
    return data
  },

  addItem: async (orderId: string, itemData: any) => {
    const { data } = await api.post(`/api/orders/${orderId}/items`, itemData)
    return data
  },

  updateItem: async (orderId: string, itemId: string, itemData: any) => {
    const { data } = await api.put(`/api/orders/${orderId}/items/${itemId}`, itemData)
    return data
  },

  removeItem: async (orderId: string, itemId: string) => {
    await api.delete(`/api/orders/${orderId}/items/${itemId}`)
  },
}

export const bindersApi = {
  list: async (): Promise<BinderSummary[]> => {
    const { data } = await api.get('/api/binders')
    return data.binders
  },

  get: async (id: number): Promise<BinderDetailResponse> => {
    const { data } = await api.get(`/api/binders/${id}`)
    return data
  },

  create: async (input: CreateBinderInput): Promise<BinderSummary> => {
    const { data } = await api.post('/api/binders', input)
    return data
  },

  update: async (id: number, input: UpdateBinderInput): Promise<UpdateBinderResponse> => {
    const { data } = await api.patch(`/api/binders/${id}`, input)
    return data
  },

  remove: async (id: number): Promise<void> => {
    await api.delete(`/api/binders/${id}`)
  },

  saveSlots: async (
    id: number,
    slots: BinderSlotInput[],
  ): Promise<{ slots: BinderSlotView[] }> => {
    const { data } = await api.put(`/api/binders/${id}/slots`, { slots })
    return data
  },

  bulkAdd: async (
    id: number,
    input: BulkAddInput,
  ): Promise<BulkAddResponse> => {
    const { data } = await api.post(`/api/binders/${id}/bulk-add`, input)
    return data
  },
}

export const bulkboxesApi = {
  list: async (): Promise<BulkboxSummary[]> => {
    const { data } = await api.get('/api/bulkboxes')
    return data.bulkboxes
  },

  get: async (id: number): Promise<BulkboxDetailResponse> => {
    const { data } = await api.get(`/api/bulkboxes/${id}`)
    return data
  },

  create: async (input: CreateBulkboxInput): Promise<BulkboxDetailResponse> => {
    const { data } = await api.post('/api/bulkboxes', input)
    return data
  },

  update: async (
    id: number,
    input: UpdateBulkboxInput,
  ): Promise<BulkboxDetailResponse> => {
    const { data } = await api.patch(`/api/bulkboxes/${id}`, input)
    return data
  },

  remove: async (id: number): Promise<void> => {
    await api.delete(`/api/bulkboxes/${id}`)
  },

  addGroup: async (
    id: number,
    input: AddGroupInput,
  ): Promise<BulkboxGroupsResponse> => {
    const { data } = await api.post(`/api/bulkboxes/${id}/groups`, input)
    return data
  },

  updateGroup: async (
    id: number,
    gid: number,
    quantity: number,
  ): Promise<BulkboxGroupsResponse> => {
    const { data } = await api.patch(`/api/bulkboxes/${id}/groups/${gid}`, {
      quantity,
    })
    return data
  },

  removeGroup: async (
    id: number,
    gid: number,
  ): Promise<BulkboxGroupsResponse> => {
    const { data } = await api.delete(`/api/bulkboxes/${id}/groups/${gid}`)
    return data
  },

  replaceGroups: async (
    id: number,
    input: ReplaceGroupsInput,
  ): Promise<BulkboxGroupsResponse> => {
    const { data } = await api.put(`/api/bulkboxes/${id}/groups`, input)
    return data
  },
}

export const storeApi = {
  // Authenticated: stock card browser
  getStockCards: async (params?: { search?: string; set_id?: string; limit?: number; offset?: number }) => {
    const { data } = await api.get('/api/store/stock-cards', { params })
    return data
  },
  // Authenticated: stock sealed browser
  getStockSealed: async (params?: { search?: string; set_id?: string; product_type?: string; limit?: number; offset?: number }) => {
    const { data } = await api.get('/api/store/stock-sealed', { params })
    return data
  },
  bulkList: async (items: { card_id?: string; slab_id?: number; sealed_product_id?: string; condition: string; price: number; quantity?: number; is_nfs?: boolean; notes?: string }[]) => {
    const { data } = await api.post('/api/store/bulk-list', { items })
    return data
  },
  // Authenticated: manage own listings.
  // Backend returns `{ items, total, limit, offset }` per docs/store-pos-api-spec.md §3.
  // We return `items` directly for backward compat with existing callers
  // that expect an array. New callers needing pagination metadata should
  // use `storeApi.getListingsPage` (returns the full response).
  getListings: async (params?: {
    status?: ListingStatus | 'any'
    condition?: ListingCondition
    language?: string
    set_id?: string
    card_id?: string
    search?: string
    limit?: number
    offset?: number
    sort?: 'updated_at' | 'price' | 'condition' | 'name'
    direction?: 'asc' | 'desc'
  }): Promise<StoreListing[]> => {
    const { data } = await api.get('/api/store/listings', { params })
    if (Array.isArray(data)) return data
    return Array.isArray(data?.items) ? data.items : []
  },
  /**
   * Paginated variant — returns the full `{ items, total, limit, offset }`
   * response. Use for infinite scroll / paged tables.
   */
  getListingsPage: async (params?: {
    status?: ListingStatus | 'any'
    condition?: ListingCondition
    language?: string
    set_id?: string
    card_id?: string
    search?: string
    limit?: number
    offset?: number
    sort?: 'updated_at' | 'price' | 'condition' | 'name'
    direction?: 'asc' | 'desc'
  }): Promise<ListingsListResponse> => {
    const { data } = await api.get('/api/store/listings', { params })
    if (Array.isArray(data)) {
      return { items: data, total: data.length, limit: data.length, offset: 0 }
    }
    return data
  },
  createListing: async (listing: { card_id?: string; slab_id?: number; sealed_product_id?: string; condition: string; price: number; quantity?: number; is_nfs?: boolean; notes?: string; language?: string; foil?: boolean; status?: 'active' | 'paused' }) => {
    const { data } = await api.post('/api/store/listings', listing)
    return data
  },
  updateListing: async (id: number, updates: { price?: number; quantity?: number; is_nfs?: boolean; notes?: string; condition?: string }) => {
    const { data } = await api.put(`/api/store/listings/${id}`, updates)
    return data
  },
  deleteListing: async (id: number) => {
    await api.delete(`/api/store/listings/${id}`)
  },
  deleteAllListings: async () => {
    const { data } = await api.delete('/api/store/listings/all')
    return data as { success: boolean; deleted: number }
  },
  // Authenticated: order requests
  getOrderRequests: async (status?: string) => {
    const { data } = await api.get('/api/store/orders', { params: status ? { status } : undefined })
    return data
  },
  getOrderRequest: async (id: number) => {
    const { data } = await api.get(`/api/store/orders/${id}`)
    return data
  },
  updateOrderRequest: async (id: number, update: {
    status: 'accepted' | 'declined' | 'packing' | 'shipped' | 'delivered'
    tracking_number?: string
    shipping_notes?: string
    notes?: string
  }) => {
    const { data } = await api.patch(`/api/store/orders/${id}`, update)
    return data
  },
  updateOrderNotes: async (id: number, fields: { notes?: string; tracking_number?: string; shipping_notes?: string }) => {
    const { data } = await api.patch(`/api/store/orders/${id}/notes`, fields)
    return data
  },
  convertToOrder: async (id: number) => {
    const { data } = await api.post(`/api/store/orders/${id}/convert`)
    return data
  },
  getStockWarnings: async (): Promise<Array<{ listing_id: number; type: 'single' | 'slab' | 'sealed'; item_name: string; listed_qty: number; actual_stock: number; deficit: number }>> => {
    const { data } = await api.get('/api/store/stock-warnings')
    return data
  },
  adjustListingQty: async (listingId: number, qty: number) => {
    const { data } = await api.put(`/api/store/listings/${listingId}`, { quantity: qty })
    return data
  },
  // Public
  getStoreInfo: async (slug: string) => {
    const { data } = await api.get(`/api/store/public/${slug}`)
    return data
  },
  getPublicListings: async (slug: string, params?: {
    search?: string
    q?: string
    set_id?: string
    rarity?: string
    condition?: string
    language?: string
    foil?: boolean | '1' | '0'
    type?: string
    listing_type?: 'cards' | 'slabs' | 'sealed'
    min_price?: number
    max_price?: number
    limit?: number
    offset?: number
  }) => {
    // Server returns spec-§3 envelope `{ items, total, limit, offset }` plus
    // a legacy `listings` alias. Normalize defensively for callers expecting
    // either shape.
    const cleaned: Record<string, string | number | boolean> = {}
    if (params) {
      for (const [k, v] of Object.entries(params)) {
        if (v === undefined || v === null || v === '') continue
        cleaned[k] = typeof v === 'boolean' ? (v ? '1' : '0') : v as string | number
      }
    }
    const { data } = await api.get(`/api/store/public/${slug}/listings`, { params: cleaned })
    if (Array.isArray(data)) {
      return { items: data, listings: data, total: data.length, limit: data.length, offset: 0 }
    }
    const items = Array.isArray(data?.items) ? data.items : (Array.isArray(data?.listings) ? data.listings : [])
    return {
      items,
      listings: items, // legacy alias for older callers
      total: typeof data?.total === 'number' ? data.total : items.length,
      limit: typeof data?.limit === 'number' ? data.limit : items.length,
      offset: typeof data?.offset === 'number' ? data.offset : 0,
    }
  },
  submitOrder: async (slug: string, order: { buyer_name: string; buyer_email: string; buyer_phone?: string; message?: string; items: { listing_id: number; quantity: number }[] }) => {
    const { data } = await api.post(`/api/store/public/${slug}/order`, order)
    return data
  },
  getPublicOrderStatus: async (slug: string, orderId: number, email: string) => {
    const { data } = await api.get(`/api/store/public/${slug}/order-status/${orderId}`, { params: { email } })
    return data
  },
  // Authenticated buyer-side cart for the public storefront.
  getPublicCart: async (slug: string) => {
    const { data } = await api.get(`/api/store/public/${slug}/cart`)
    return data as { cart_id: number; items: PublicStoreCartItem[] }
  },
  replacePublicCart: async (slug: string, items: { listing_id: number; quantity: number }[]) => {
    const { data } = await api.put(`/api/store/public/${slug}/cart`, { items })
    return data as { cart_id: number; items: PublicStoreCartItem[] }
  },
  addPublicCartItem: async (slug: string, listing_id: number, quantity = 1) => {
    const { data } = await api.post(`/api/store/public/${slug}/cart/items`, { listing_id, quantity })
    return data as { cart_id: number; items: PublicStoreCartItem[]; capped: boolean; capped_quantity: number | null }
  },
  updatePublicCartItem: async (slug: string, lineId: number, quantity: number) => {
    const { data } = await api.patch(`/api/store/public/${slug}/cart/items/${lineId}`, { quantity })
    return data as { cart_id: number; items: PublicStoreCartItem[] }
  },
  removePublicCartItem: async (slug: string, lineId: number) => {
    const { data } = await api.delete(`/api/store/public/${slug}/cart/items/${lineId}`)
    return data as { cart_id: number; items: PublicStoreCartItem[] }
  },
  mergePublicCart: async (slug: string, items: { listing_id: number; quantity: number }[]) => {
    const { data } = await api.post(`/api/store/public/${slug}/cart/merge`, { items })
    return data as {
      cart_id: number
      items: PublicStoreCartItem[]
      capped: Array<{ listing_id: number; quantity: number; capped_by: number; available_qty: number }>
      dropped: Array<{ listing_id: number; quantity: number }>
    }
  },
  // Authenticated: analytics
  getAnalytics: async () => {
    const { data } = await api.get('/api/store/analytics')
    return data as {
      total_revenue: number
      revenue_this_month: number
      revenue_last_month: number
      revenue_trend: number
      total_orders: number
      orders_by_status: Record<string, number>
      avg_order_value: number
      orders_this_month: number
      total_listings: number
      total_listed_value: number
      nfs_count: number
      top_cards: Array<{
        card_id: string
        card_name: string
        set_name: string
        total_sold: number
        total_revenue: number
      }>
      revenue_by_month: Array<{
        month: string
        revenue: number
        order_count: number
      }>
      recent_orders: Array<{
        id: number
        buyer_name: string
        total_amount: number
        status: string
        created_at: string
      }>
    }
  },
  // Authenticated: store customization
  getSettings: async () => {
    const { data } = await api.get('/api/store/settings')
    return data as { description: string | null; policies: string | null; contact_info: string | null; announcement: string | null; banner_url: string | null; vacation_start: string | null; vacation_end: string | null; vacation_message: string | null }
  },
  updateSettings: async (settings: { description?: string; policies?: string; contact_info?: string; announcement?: string; banner_url?: string; vacation_start?: string | null; vacation_end?: string | null; vacation_message?: string | null }) => {
    const { data } = await api.put('/api/store/settings', settings)
    return data
  },
  // Authenticated: unified inventory overview
  getInventoryOverview: async (params?: {
    search?: string
    type?: 'single' | 'slab' | 'sealed'
    sort?: 'name' | 'margin' | 'value' | 'qty' | 'listed_qty' | 'cost'
    direction?: 'asc' | 'desc'
    listed?: 'true' | 'false'
    profitable?: 'true' | 'false'
  }) => {
    const { data } = await api.get('/api/store/inventory-overview', { params })
    return data as {
      items: Array<{
        type: 'single' | 'slab' | 'sealed'
        id: string
        name: string
        image_url: string | null
        set_id: string | null
        set_name: string | null
        owned_qty: number
        listed_qty: number
        available_qty: number
        cost_basis: number | null
        market_price: number | null
        listed_price: number | null
        margin: number | null
        listings: Array<{ id: number; condition: string; price: number; quantity: number; is_nfs: boolean }>
        rarity: string | null
        card_number: string | null
        grading_company: string | null
        grade: number | null
        product_type: string | null
      }>
      summary: {
        total_items: number
        listed_items: number
        total_cost_basis: number
        total_listed_value: number
        total_market_value: number
      }
    }
  },
}

// ---------------------------------------------------------------------------
// Store pricing & extended store endpoints (per docs/store-pos-api-spec.md §3)
// ---------------------------------------------------------------------------

export interface StockCardsParams {
  search?: string
  set_id?: string
  condition?: ListingCondition
  language?: string
  foil?: 'true' | 'false' | 'any'
  min_qty?: number
  min_market_price?: number
  has_listing?: 'true' | 'false'
  limit?: number
  offset?: number
}

export interface ListingsListParams {
  status?: ListingStatus | 'any'
  condition?: ListingCondition
  language?: string
  set_id?: string
  search?: string
  limit?: number
  offset?: number
  sort?: 'updated_at' | 'price' | 'condition' | 'name'
  direction?: 'asc' | 'desc'
}

export interface QuickAddListingPayload {
  card_id?: string
  set_id?: string
  number?: string
  condition: ListingCondition
  language?: string
  foil?: boolean
  quantity?: number
  price?: number
  use_suggested?: boolean
  notes?: string
}

export interface PriceSuggestParams {
  card_id?: string
  sealed_product_id?: string
  slab_id?: number
  condition?: ListingCondition
  language?: string
  foil?: boolean
}

export interface PriceSuggestBulkPayload {
  items: PriceSuggestParams[]
}

/**
 * Store pricing + listing-management API. Used by `/store/manage/*`
 * (`StoreInventoryPage`, `StorePricingPage`) and the POS lookup flow.
 * See `docs/store-management.md` and `docs/store-pos-api-spec.md` §3.
 */
export const storePricingApi = {
  /** GET store config + per-vendor pricing defaults + feature flags. */
  getSettingsExtended: async (): Promise<StoreSettingsExtended> => {
    const { data } = await api.get('/api/store/settings/extended')
    return data
  },
  /** Persist per-vendor pricing defaults (markup, floor, rounding, condition multipliers). */
  updatePricingDefaults: async (defaults: PricingDefaults): Promise<{ success: boolean; defaults: PricingDefaults }> => {
    const { data } = await api.put('/api/store/pricing-defaults', defaults)
    return data
  },

  /** Paginated stock-card browser (only stock-type collections). */
  getStockCards: async (params?: StockCardsParams): Promise<StockCardsResponse> => {
    const { data } = await api.get('/api/store/stock-cards', { params })
    return data
  },
  /** Paginated listings list with filters (status, condition, language, set_id, search). Default status=active. */
  getListings: async (params?: ListingsListParams): Promise<ListingsListResponse> => {
    const { data } = await api.get('/api/store/listings', { params })
    return data
  },
  /** Lifecycle transition. Rejects 'sold' (terminal, set internally on POS / order conversion). */
  setListingStatus: async (id: number, status: ListingStatus): Promise<{ success: boolean; listing: StoreListing }> => {
    const { data } = await api.patch(`/api/store/listings/${id}/status`, { status })
    return data
  },
  /** Search-and-list one-shot. Pass `use_suggested=true` to auto-fill price from the waterfall. */
  quickAddListing: async (payload: QuickAddListingPayload): Promise<StoreListing> => {
    const { data } = await api.post('/api/store/listings/quick-add', payload)
    return data
  },

  /** Apply a bulk pricing operation; pass `dry_run=true` for a preview-only response. */
  bulkPrice: async (req: BulkPriceRequest): Promise<BulkPriceResponse> => {
    const { data } = await api.post('/api/store/listings/bulk-price', req)
    return data
  },
  /** Flip many listings active ↔ paused via filter or explicit IDs. */
  bulkStatus: async (req: { listing_ids?: number[]; filter?: BulkPriceRequest['filter']; status: 'active' | 'paused' }): Promise<{ matched: number; updated: number }> => {
    const { data } = await api.post('/api/store/listings/bulk-status', req)
    return data
  },

  /** Single fused price suggestion via the manual → CT → CM → cost-basis → floor waterfall. */
  suggestPrice: async (params: PriceSuggestParams): Promise<PriceSuggestion> => {
    const { data } = await api.get('/api/store/pricing/suggest', { params })
    return data
  },
  /** Batch price suggestions, max 500 items per call. */
  suggestPriceBulk: async (payload: PriceSuggestBulkPayload): Promise<{ suggestions: PriceSuggestion[] }> => {
    const { data } = await api.post('/api/store/pricing/suggest-bulk', payload)
    return data
  },

  /** Per-condition / language / foil breakdown for a single card (stock + listed + reserved). */
  getStockBreakdown: async (cardId: string): Promise<StockBreakdown> => {
    const { data } = await api.get(`/api/store/stock-cards/${cardId}/breakdown`)
    return data
  },

  /** Place a temporary hold on a listing (TTL clamped server-side to 1-1440 minutes). */
  createReservation: async (listingId: number, payload: { quantity: number; ttl_minutes?: number; note?: string }): Promise<{ reservation_id: number; listing_id: number; quantity: number; expires_at: string }> => {
    const { data } = await api.post(`/api/store/listings/${listingId}/reserve`, payload)
    return data
  },
  /** Release a hold early (vendor-only). */
  releaseReservation: async (reservationId: number): Promise<{ success: boolean }> => {
    const { data } = await api.delete(`/api/store/reservations/${reservationId}`)
    return data
  },
  /** List active reservations; pass `expired:'true'` to include expired rows. */
  getReservations: async (params?: { expired?: 'true' | 'false' }): Promise<{ items: Reservation[] }> => {
    const { data } = await api.get('/api/store/reservations', { params })
    return data
  },
}

// ---------------------------------------------------------------------------
// POS endpoints (per docs/store-pos-api-spec.md §4)
// ---------------------------------------------------------------------------

export interface PosLookupParams {
  q: string
  mode?: 'auto' | 'card' | 'sealed' | 'slab' | 'barcode'
  limit?: number
}

export interface PosCartLineCreateListing {
  kind: 'listing'
  listing_id: number
  quantity: number
  override_price?: number
}

export interface PosCartLineCreateStock {
  kind: 'stock'
  card_id?: string
  sealed_product_id?: string
  slab_id?: number
  condition: ListingCondition
  language?: string
  foil?: boolean
  quantity: number
  price: number
}

export interface PosCartLineCreateAdhoc {
  kind: 'adhoc'
  label: string
  price: number
  quantity: number
}

export type PosCartLineCreate =
  | PosCartLineCreateListing
  | PosCartLineCreateStock
  | PosCartLineCreateAdhoc

export interface PosTradeInPayload {
  items: Array<{
    card_id?: string
    sealed_product_id?: string
    label?: string
    condition?: ListingCondition
    language?: string
    foil?: boolean
    quantity: number
    credit_per_unit: number
    target_collection_id?: number
  }>
}

export interface PosTransactionsListParams {
  limit?: number
  offset?: number
  from?: string
  to?: string
  payment_method?: PosPaymentMethod
  session_id?: number
  search?: string
}

/**
 * Point-of-Sale API. All endpoints require vendor (store_slug + store_enabled).
 * See `docs/pos.md` and `docs/store-pos-api-spec.md` §4.
 */
export const posApi = {
  /** Open a new POS session. Idempotent: returns the existing open one if any (200 vs 201). */
  openSession: async (payload?: { name?: string; location?: string; cash_float?: number }): Promise<PosSession> => {
    const { data } = await api.post('/api/pos/sessions', payload || {})
    return data
  },
  /** Current open session for the vendor, or `null`. */
  getActiveSession: async (): Promise<PosSession | null> => {
    const { data } = await api.get('/api/pos/sessions/active')
    return data
  },
  /** Fetch a session (with cart) by id. Must own. */
  getSession: async (id: number): Promise<PosSession> => {
    const { data } = await api.get(`/api/pos/sessions/${id}`)
    return data
  },
  /** Edit name / location / cash float / notes on an open session. */
  updateSession: async (id: number, patch: { name?: string; location?: string; cash_float?: number; notes?: string }): Promise<PosSession> => {
    const { data } = await api.patch(`/api/pos/sessions/${id}`, patch)
    return data
  },
  /** Close session and return the Z-report summary (cash variance included if `cash_count` provided). */
  closeSession: async (id: number, payload?: { notes?: string; cash_count?: number }): Promise<{ success: boolean; session: PosSession; summary: PosSessionSummary }> => {
    const { data } = await api.post(`/api/pos/sessions/${id}/close`, payload || {})
    return data
  },
  /** List sessions; filter by status/date range. */
  listSessions: async (params?: { status?: 'open' | 'closed' | 'all'; from?: string; to?: string; limit?: number; offset?: number }): Promise<{ items: PosSession[]; total: number }> => {
    const { data } = await api.get('/api/pos/sessions', { params })
    return data
  },
  /** Recompute session summary at any time (close not required). */
  sessionSummary: async (id: number): Promise<PosSessionSummary> => {
    const { data } = await api.get(`/api/pos/sessions/${id}/summary`)
    return data
  },

  /** Multi-modal stock lookup (cards / sealed / slabs in stock collections). q must be ≥ 2 chars. */
  lookup: async (params: PosLookupParams): Promise<{ results: PosLookupResult[] }> => {
    const { data } = await api.get('/api/pos/lookup', { params })
    return data
  },

  /** Add a line (kind: listing | stock | adhoc). Quantity must be 1-9999. */
  addCartLine: async (sessionId: number, line: PosCartLineCreate): Promise<PosSession> => {
    const { data } = await api.post(`/api/pos/sessions/${sessionId}/cart/items`, line)
    return data
  },
  /** Edit quantity / price / note on a cart line; rejects if line already finalized. */
  updateCartLine: async (sessionId: number, lineId: number, patch: { quantity?: number; price?: number; note?: string }): Promise<PosSession> => {
    const { data } = await api.patch(`/api/pos/sessions/${sessionId}/cart/items/${lineId}`, patch)
    return data
  },
  /** Remove a cart line (only while unfinalized). */
  removeCartLine: async (sessionId: number, lineId: number): Promise<PosSession> => {
    const { data } = await api.delete(`/api/pos/sessions/${sessionId}/cart/items/${lineId}`)
    return data
  },
  /** Set or replace the cart's single discount line (pct or eur; pass value=0 to clear). */
  setCartDiscount: async (sessionId: number, discount: { kind: 'pct' | 'eur'; value: number; label?: string }): Promise<PosSession> => {
    const { data } = await api.post(`/api/pos/sessions/${sessionId}/cart/discount`, discount)
    return data
  },
  /** Add trade-in lines; on checkout, written to inventory_intake (source 'pos_trade_in') if subject + collection set. */
  addTradeIn: async (sessionId: number, payload: PosTradeInPayload): Promise<PosSession> => {
    const { data } = await api.post(`/api/pos/sessions/${sessionId}/cart/trade-in`, payload)
    return data
  },
  /** Clear all unfinalized cart lines and release any session reservations. */
  clearCart: async (sessionId: number): Promise<PosSession> => {
    const { data } = await api.delete(`/api/pos/sessions/${sessionId}/cart`)
    return data
  },

  /** Finalise the cart into a transaction + order. Send `Idempotency-Key` header for safe retry (24h replay). */
  checkout: async (sessionId: number, payload: PosCheckoutRequest): Promise<PosCheckoutResponse> => {
    const { data } = await api.post(`/api/pos/sessions/${sessionId}/checkout`, payload)
    return data
  },
  /** Discard the in-flight cart without finalising. */
  voidCart: async (sessionId: number): Promise<PosSession> => {
    const { data } = await api.post(`/api/pos/sessions/${sessionId}/void`, {})
    return data
  },
  /** Refund full or partial. Restocks unless `restock=false`; partial refunds IDOR-checked per cart line. */
  refundTransaction: async (transactionId: number, payload: { kind: 'full' | 'partial'; line_refunds?: Array<{ pos_cart_line_id: number; quantity: number }>; reason?: string; restock?: boolean }): Promise<PosRefund> => {
    const { data } = await api.post(`/api/pos/transactions/${transactionId}/refund`, payload)
    return data
  },

  /** List past POS transactions with summary (gross / net / count / avg basket). */
  listTransactions: async (params?: PosTransactionsListParams): Promise<{
    items: PosTransactionSummary[]
    total: number
    summary: { gross_revenue: number; net_revenue: number; transaction_count: number; avg_basket: number }
  }> => {
    const { data } = await api.get('/api/pos/transactions', { params })
    return data
  },
  /** Single transaction with full line breakdown (post-finalization). */
  getTransaction: async (id: number): Promise<PosTransaction> => {
    const { data } = await api.get(`/api/pos/transactions/${id}`)
    return data
  },
}

export const collectionApi = {
  getAll: async (collectionId?: string | number) => {
    const params = collectionId ? { collection_id: collectionId } : {}
    const { data } = await api.get<CollectionItem[]>('/api/collection', { params })
    return data
  },
  getCards: async (collectionId?: string | number) => {
    const params = collectionId ? { collection_id: collectionId } : {}
    const { data } = await api.get('/api/collection/cards', { params })
    return data
  },
  add: async (cardId: string, quantity: number = 1, foilQuantity: number = 0, collectionId?: number) => {
    const payload: any = {
      card_id: cardId,
      quantity,
      foil_quantity: foilQuantity,
    }
    // Only include collection_id if explicitly provided
    if (collectionId !== undefined) {
      payload.collection_id = collectionId
    }
    const { data } = await api.post('/api/collection', payload)
    return data
  },
  update: async (id: number, quantity: number, foilQuantity: number) => {
    const { data } = await api.put(`/api/collection/${id}`, {
      quantity,
      foil_quantity: foilQuantity,
    })
    return data
  },
  remove: async (id: number) => {
    const { data } = await api.delete(`/api/collection/${id}`)
    return data
  },
  getStats: async (collectionId?: string | number) => {
    const params = collectionId ? { collection_id: collectionId } : {}
    const { data } = await api.get<CollectionStats>('/api/collection/stats', { params })
    return data
  },
}

export const collectionsApi = {
  getAll: async () => {
    const { data } = await api.get('/api/collections')
    return data
  },
  getById: async (id: string | number) => {
    const { data } = await api.get(`/api/collections/${id}`)
    return data
  },
  create: async (name: string, description?: string, collection_type?: string) => {
    const { data } = await api.post('/api/collections', { name, description, collection_type })
    return data
  },
  update: async (id: string | number, name: string, description?: string, collection_type?: string) => {
    const { data } = await api.put(`/api/collections/${id}`, { name, description, collection_type })
    return data
  },
  delete: async (id: string | number, purge = false, moveTo?: number) => {
    const params = new URLSearchParams()
    if (purge) params.set('purge', 'true')
    if (moveTo) params.set('move_to', String(moveTo))
    const qs = params.toString()
    const { data } = await api.delete(`/api/collections/${id}${qs ? '?' + qs : ''}`)
    return data
  },
  toggleLock: async (id: string | number) => {
    const { data } = await api.put(`/api/collections/${id}/toggle-lock`)
    return data
  },
  toggleHidden: async (id: string | number) => {
    const { data } = await api.put(`/api/collections/${id}/toggle-hidden`)
    return data
  },
  getMergedStock: async () => {
    const { data } = await api.get('/api/collections/merged/total-stock')
    return data
  },
  getCardBreakdown: async (cardId: string) => {
    const { data } = await api.get(`/api/collections/card/${cardId}/breakdown`)
    return data
  },
  setDefault: async (id: string | number) => {
    const { data } = await api.put(`/api/collections/${id}/set-default`)
    return data
  },
  reorder: async (order: (string | number)[]) => {
    const { data } = await api.put('/api/collections/reorder', { order })
    return data
  },
  // Sub-collections
  getSubCollections: async (collectionId: number) => {
    const { data } = await api.get(`/api/collections/${collectionId}/sub-collections`)
    return data
  },
  createSubCollection: async (collectionId: number, name: string, type: string = 'other', description?: string) => {
    const { data } = await api.post(`/api/collections/${collectionId}/sub-collections`, { name, type, description })
    return data
  },
  updateSubCollection: async (collectionId: number, subId: number, name: string, type?: string, description?: string) => {
    const { data } = await api.put(`/api/collections/${collectionId}/sub-collections/${subId}`, { name, type, description })
    return data
  },
  deleteSubCollection: async (collectionId: number, subId: number) => {
    const { data } = await api.delete(`/api/collections/${collectionId}/sub-collections/${subId}`)
    return data
  },
  assignSubCollection: async (cardIds: string[], collectionId: number, subCollectionId: number | null) => {
    const { data } = await api.post('/api/collections/assign-sub-collection', {
      card_ids: cardIds,
      collection_id: collectionId,
      sub_collection_id: subCollectionId,
    })
    return data
  },
  moveCards: async (cardIds: string[], sourceCollectionId: number, targetCollectionId: number, quantities?: Record<string, { quantity?: number; foil_quantity?: number }>) => {
    const { data } = await api.post('/api/collections/move-cards', {
      card_ids: cardIds,
      source_collection_id: sourceCollectionId,
      target_collection_id: targetCollectionId,
      ...(quantities ? { quantities } : {}),
    })
    return data
  },
  // Value dashboard
  getValueDashboard: async () => {
    const { data } = await api.get('/api/collections/value/dashboard')
    return data
  },
  getTopValueCards: async (params?: { collection_id?: number; sub_collection_id?: number; set_id?: string; rarity?: string; color?: string; limit?: number }) => {
    const { data } = await api.get('/api/collections/value/top-cards', { params })
    return data
  },
  getCollectionDetailStats: async (collectionId: number) => {
    const { data } = await api.get(`/api/collections/value/collection/${collectionId}/stats`)
    return data
  },
  importTcgPowerTools: async (file: File) => {
    const formData = new FormData()
    formData.append('file', file)
    const { data } = await api.post('/api/collections/import/tcgpowertools', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    })
    return data as {
      success: boolean
      collection_id: number
      imported: number
      total_rows: number
      unmapped_count: number
      unmapped_sample: { name: string; setCode: string; cn: string; reason: string }[]
    }
  },
  compareCollections: async (idA: number, idB: number) => {
    const { data } = await api.get(`/api/collections/compare/${idA}/${idB}`)
    return data
  },
}

export const decksApi = {
  getAll: async () => {
    const { data } = await api.get('/api/decks')
    return data
  },
  getById: async (id: string) => {
    const { data } = await api.get(`/api/decks/${id}`)
    return data
  },
  create: async (deck: { name: string; description?: string; leader_card_id: string; format?: string }) => {
    const { data } = await api.post('/api/decks', deck)
    return data
  },
  update: async (id: string, deck: { name: string; description?: string; leader_card_id: string; format?: string }) => {
    const { data } = await api.put(`/api/decks/${id}`, deck)
    return data
  },
  delete: async (id: string) => {
    const { data } = await api.delete(`/api/decks/${id}`)
    return data
  },
  addCard: async (deckId: string, cardId: string, quantity: number) => {
    const { data } = await api.post(`/api/decks/${deckId}/cards`, {
      card_id: cardId,
      quantity,
    })
    return data
  },
  removeCard: async (deckId: string, cardId: string) => {
    const { data } = await api.delete(`/api/decks/${deckId}/cards/${cardId}`)
    return data
  },
  swapCardVariant: async (deckId: string, cardId: string, newCardId: string) => {
    const { data } = await api.put(`/api/decks/${deckId}/cards/${cardId}/swap`, {
      new_card_id: newCardId,
    })
    return data
  },
  validate: async (deckId: string) => {
    const { data } = await api.post(`/api/decks/${deckId}/validate`)
    return data
  },
}

export const cardtraderApi = {
  getSettings: async () => {
    const { data } = await api.get('/api/cardtrader/settings')
    return data
  },
  updateSettings: async (settings: {
    jwt_token?: string
    username?: string
    auto_sync_enabled?: boolean
    sync_interval_hours?: number
    webhook_enabled?: boolean
    webhook_secret?: string
  }) => {
    // Backend expects camelCase keys
    const payload: any = {}
    if (settings.jwt_token !== undefined) payload.jwtToken = settings.jwt_token
    if (settings.username !== undefined) payload.username = settings.username
    if (settings.auto_sync_enabled !== undefined) payload.autoSyncEnabled = settings.auto_sync_enabled
    if (settings.sync_interval_hours !== undefined) payload.syncIntervalHours = settings.sync_interval_hours
    if (settings.webhook_enabled !== undefined) payload.webhookEnabled = settings.webhook_enabled
    if (settings.webhook_secret !== undefined) payload.webhookSecret = settings.webhook_secret
    const { data } = await api.put('/api/cardtrader/settings', payload)
    return data
  },
  testConnection: async () => {
    const { data } = await api.post('/api/cardtrader/test-connection')
    return data
  },
  syncInventory: async () => {
    const { data } = await api.post('/api/cardtrader/sync-inventory')
    return data
  },
  getInventory: async () => {
    const { data } = await api.get('/api/cardtrader/inventory')
    return data
  },
  getSyncStatus: async () => {
    const { data } = await api.get('/api/cardtrader/sync-status')
    return data
  },
  syncAllPrices: async () => {
    const { data } = await api.post('/api/admin/cardtrader/sync-prices')
    return data
  },
  syncCardPrice: async (cardId: string) => {
    const { data } = await api.post(`/api/cardtrader/cards/${cardId}/sync-price`)
    return data
  },
  getCardPrice: async (cardId: string) => {
    try {
      const { data } = await api.get(`/api/cardtrader/cards/${cardId}/prices`)
      return { price: data }
    } catch (err: any) {
      if (err.response?.status === 404) return { price: null }
      throw err
    }
  },
  getCardPriceHistory: async (cardId: string, days?: number) => {
    try {
      const params = days ? { days } : {}
      const { data } = await api.get(`/api/cardtrader/cards/${cardId}/price-history`, { params })
      return data
    } catch (err: any) {
      if (err.response?.status === 404) return { card_id: cardId, days: days || 30, history: [] }
      throw err
    }
  },
  getAllPrices: async (): Promise<Record<string, { min: number; avg: number; median: number; currency: string }>> => {
    const { data } = await api.get('/api/cardtrader/prices')
    return data
  },
  getInventoryPrices: async (): Promise<Record<string, number>> => {
    const { data } = await api.get('/api/cardtrader/inventory-prices')
    return data
  },
  importBlueprints: async () => {
    const { data } = await api.post('/api/admin/cardtrader/import-blueprints')
    return data
  },
  remapBlueprints: async () => {
    const { data } = await api.post('/api/admin/cardtrader/map-blueprints', { remapAll: true })
    return data
  },
  getManualPrices: async () => {
    const { data } = await api.get('/api/admin/cardtrader/manual-prices')
    return data as Array<{ card_id: string; price: number; updated_at: string; card_name: string; image_url: string; set_id: string }>
  },
  setManualPrice: async (cardId: string, price: number) => {
    const { data } = await api.post('/api/admin/cardtrader/manual-price', { card_id: cardId, price })
    return data
  },
  deleteManualPrice: async (cardId: string) => {
    const { data } = await api.delete(`/api/admin/cardtrader/manual-price/${cardId}`)
    return data
  },
}

export const inventoryApi = {
  getRecords: async (params?: { card_id?: string; collection_id?: number; source?: string; limit?: number; offset?: number }) => {
    const { data } = await api.get('/api/inventory', { params })
    return data
  },

  getLedger: async (params?: { limit?: number; offset?: number; direction?: string; source?: string; card_id?: string }) => {
    const { data } = await api.get('/api/inventory/ledger', { params })
    return data
  },

  getStats: async () => {
    const { data } = await api.get('/api/inventory/stats')
    return data
  },

  intake: async (items: IntakeItem[], collection_id?: number, source?: string, notes?: string) => {
    const { data } = await api.post('/api/inventory/intake', { items, collection_id, source, notes })
    return data
  },

  delete: async (id: number) => {
    const { data } = await api.delete(`/api/inventory/${id}`)
    return data
  },

  getNegativeStock: async () => {
    const { data } = await api.get('/api/inventory/negative-stock')
    return data
  },

  zeroOutNegativeStock: async () => {
    const { data } = await api.post('/api/inventory/negative-stock/zero-out')
    return data
  },

  updateLedgerEntry: async (sourceTable: string, sourceId: number, updates: { price_per_card?: number; quantity?: number; foil_quantity?: number; collection_id?: number | null }) => {
    const { data } = await api.put(`/api/inventory/ledger/${sourceTable}/${sourceId}`, updates)
    return data
  },

  deleteLedgerEntry: async (sourceTable: string, sourceId: number) => {
    const { data } = await api.delete(`/api/inventory/ledger/${sourceTable}/${sourceId}`)
    return data
  },

  batchLedger: async (action: 'delete' | 'update', entries: Array<{ source_table: string; source_id: number }>, updates?: { price_per_card?: number; collection_id?: number | null }) => {
    const { data } = await api.post('/api/inventory/ledger/batch', { action, entries, updates })
    return data
  },
}

export const slabsApi = {
  getAll: async (params?: { grading_company?: string; card_id?: string }) => {
    const { data } = await api.get('/api/slabs', { params })
    return data
  },

  getStats: async () => {
    const { data } = await api.get('/api/slabs/stats')
    return data
  },

  create: async (slab: any) => {
    const { data } = await api.post('/api/slabs', slab)
    return data
  },

  update: async (id: number, updates: any) => {
    const { data } = await api.put(`/api/slabs/${id}`, updates)
    return data
  },

  delete: async (id: number) => {
    const { data } = await api.delete(`/api/slabs/${id}`)
    return data
  },

  lookup: async (grading_company: string, cert_number: string) => {
    const { data } = await api.post('/api/slabs/lookup', { grading_company, cert_number })
    return data
  },

  uploadPhoto: async (id: number, file: File) => {
    const form = new FormData()
    form.append('photo', file)
    const { data } = await api.post(`/api/slabs/${id}/photo`, form, {
      headers: { 'Content-Type': 'multipart/form-data' },
    })
    return data
  },

  deletePhoto: async (id: number) => {
    const { data } = await api.delete(`/api/slabs/${id}/photo`)
    return data
  },
}

export const breaksApi = {
  getAll: async (status?: string) => {
    const params = status ? { status } : {}
    const { data } = await api.get('/api/breaks', { params })
    return data
  },

  getStats: async () => {
    const { data } = await api.get('/api/breaks/stats')
    return data
  },

  getById: async (id: number) => {
    const { data } = await api.get(`/api/breaks/${id}`)
    return data
  },

  create: async (name: string, notes?: string, products?: Partial<BreakProduct>[]) => {
    const { data } = await api.post('/api/breaks', { name, notes, products })
    return data
  },

  update: async (id: number, updates: { name?: string; notes?: string }) => {
    const { data } = await api.put(`/api/breaks/${id}`, updates)
    return data
  },

  delete: async (id: number) => {
    await api.delete(`/api/breaks/${id}`)
  },

  addProduct: async (breakId: number, product: { product_type: string; product_name: string; quantity: number; price_paid: number; notes?: string }) => {
    const { data } = await api.post(`/api/breaks/${breakId}/products`, product)
    return data
  },

  updateProduct: async (breakId: number, productId: number, updates: Partial<BreakProduct>) => {
    const { data } = await api.put(`/api/breaks/${breakId}/products/${productId}`, updates)
    return data
  },

  deleteProduct: async (breakId: number, productId: number) => {
    await api.delete(`/api/breaks/${breakId}/products/${productId}`)
  },

  addPull: async (breakId: number, pull: { card_id: string; quantity: number; foil_quantity?: number; destination?: string; collection_id?: number; notes?: string }) => {
    const { data } = await api.post(`/api/breaks/${breakId}/pulls`, pull)
    return data
  },

  updatePull: async (breakId: number, pullId: number, updates: { quantity?: number; foil_quantity?: number }) => {
    const { data } = await api.put(`/api/breaks/${breakId}/pulls/${pullId}`, updates)
    return data
  },

  deletePull: async (breakId: number, pullId: number) => {
    await api.delete(`/api/breaks/${breakId}/pulls/${pullId}`)
  },

  complete: async (breakId: number) => {
    const { data } = await api.post(`/api/breaks/${breakId}/complete`)
    return data
  },
}

export const wantsApi = {
  // Get all wants lists
  getAll: async () => {
    const { data } = await api.get('/api/wants')
    return data
  },

  // Create a new wants list
  create: async (data: { name: string; description?: string; is_public?: boolean }) => {
    const { data: result } = await api.post('/api/wants', data)
    return result
  },

  // Get a specific wants list
  getById: async (id: number) => {
    const { data } = await api.get(`/api/wants/${id}`)
    return data
  },

  // Update a wants list
  update: async (id: number, data: { name?: string; description?: string; is_public?: boolean }) => {
    const { data: result } = await api.put(`/api/wants/${id}`, data)
    return result
  },

  // Delete a wants list
  delete: async (id: number) => {
    const { data } = await api.delete(`/api/wants/${id}`)
    return data
  },

  // Add item to wants list
  addItem: async (listId: number, data: { card_id: string; quantity?: number; notes?: string }) => {
    const { data: result } = await api.post(`/api/wants/${listId}/items`, data)
    return result
  },

  // Update item in wants list
  updateItem: async (listId: number, itemId: number, data: { quantity?: number; status?: string; notes?: string }) => {
    const { data: result } = await api.put(`/api/wants/${listId}/items/${itemId}`, data)
    return result
  },

  // Remove item from wants list
  removeItem: async (listId: number, itemId: number) => {
    const { data } = await api.delete(`/api/wants/${listId}/items/${itemId}`)
    return data
  },

  // Get public wants list by share code
  getByShareCode: async (shareCode: string) => {
    const { data } = await api.get(`/api/wants/share/${shareCode}`)
    return data
  },

  // Get wants info for a specific card
  getCardWants: async (cardId: string) => {
    const { data } = await api.get(`/api/wants/card/${cardId}`)
    return data
  },

  // Generate collage for wants list (called when share button is clicked)
  generateCollage: async (listId: number) => {
    const { data } = await api.post(`/api/wants/${listId}/generate-collage`)
    return data
  }
}

// ============================================================================
// Sealed Products API
// ============================================================================

export const sealedApi = {
  getProducts: async (params?: { set_id?: string; product_type?: string; search?: string; language?: string; limit?: number; offset?: number }) => {
    const { data } = await api.get('/api/sealed/products', { params })
    return data
  },

  getProduct: async (id: string) => {
    const { data } = await api.get(`/api/sealed/products/${id}`)
    return data
  },

  getPrices: async () => {
    const { data } = await api.get('/api/sealed/prices')
    return data
  },

  getCollection: async () => {
    const { data } = await api.get('/api/sealed/collection')
    return data
  },

  addToCollection: async (body: { sealed_product_id: string; collection_id?: number; quantity?: number; price_paid?: number; notes?: string }) => {
    const { data } = await api.post('/api/sealed/collection', body)
    return data
  },

  updateCollectionItem: async (id: number, body: { quantity?: number; price_paid?: number; notes?: string }) => {
    const { data } = await api.put(`/api/sealed/collection/${id}`, body)
    return data
  },

  removeFromCollection: async (id: number) => {
    const { data } = await api.delete(`/api/sealed/collection/${id}`)
    return data
  },
}

export const feedbackApi = {
  submit: async (cardId: string, category: string, message: string) => {
    const { data } = await api.post('/api/feedback', { card_id: cardId, category, message })
    return data
  },
  list: async (status = 'open', page = 1) => {
    const { data } = await api.get('/api/feedback', { params: { status, page } })
    return data
  },
  update: async (id: number, status: string, adminNotes?: string) => {
    const { data } = await api.put(`/api/feedback/${id}`, { status, admin_notes: adminNotes })
    return data
  },
  delete: async (id: number) => {
    const { data } = await api.delete(`/api/feedback/${id}`)
    return data
  },
}

// ============================================================================
// Trading API
// ============================================================================
export const tradingApi = {
  getMarketOverview: async () => {
    const { data } = await api.get('/api/trading/market-overview')
    return data
  },
  getTopMovers: async (period = 7, limit = 20) => {
    const { data } = await api.get('/api/trading/top-movers', { params: { period, limit } })
    return data
  },
  getCardChart: async (cardId: string, period = 90, granularity = 'daily') => {
    const { data } = await api.get(`/api/trading/card/${cardId}/chart`, { params: { period, granularity } })
    return data
  },
  getCardListings: async (cardId: string, limit = 50) => {
    const { data } = await api.get(`/api/trading/card/${cardId}/listings`, { params: { limit } })
    return data
  },
  getHeatmap: async (period = 7) => {
    const { data } = await api.get('/api/trading/heatmap', { params: { period } })
    return data
  },
  getSetHeatmap: async (setId: string, period = 7) => {
    const { data } = await api.get(`/api/trading/heatmap/${setId}`, { params: { period } })
    return data
  },
  getMarketIndices: async (period = 90) => {
    const { data } = await api.get('/api/trading/market-indices', { params: { period } })
    return data
  },
  getScanner: async (params: Record<string, any>) => {
    const { data } = await api.get('/api/trading/scanner', { params })
    return data
  },
  getPortfolioPerformance: async (period = 90, collectionType?: string) => {
    const { data } = await api.get('/api/trading/portfolio/performance', {
      params: { period, collection_type: collectionType }
    })
    return data
  },
  getPortfolioAllocation: async (collectionType?: string) => {
    const { data } = await api.get('/api/trading/portfolio/allocation', {
      params: { collection_type: collectionType }
    })
    return data
  },
  getActivity: async (limit = 50) => {
    const { data } = await api.get('/api/trading/activity', { params: { limit } })
    return data
  },
  getSealedMarket: async () => {
    const { data } = await api.get('/api/trading/sealed/market')
    return data
  },
  getSets: async () => {
    const { data } = await api.get('/api/trading/sets')
    return data
  },
  getCardAnalytics: async (cardId: string) => {
    const { data } = await api.get(`/api/trading/card/${cardId}/analytics`)
    return data
  },
  getSetsRankings: async () => {
    const { data } = await api.get('/api/trading/sets/rankings')
    return data
  },
  getSetBreakdown: async (setId: string) => {
    const { data } = await api.get(`/api/trading/sets/${setId}/breakdown`)
    return data
  },
  getPriceSources: async (cardId: string) => {
    const { data } = await api.get(`/api/trading/price-sources/${cardId}`)
    return data
  },
  getCardTraderOverview: async () => {
    const { data } = await api.get('/api/trading/cardtrader/overview')
    return data
  },
  getRevenueTrends: async (months = 12) => {
    const { data } = await api.get('/api/trading/revenue-trends', { params: { months } })
    return data
  },
  getInventoryFlow: async (days = 90) => {
    const { data } = await api.get('/api/trading/inventory-flow', { params: { days } })
    return data
  },
  compare: async (params: { card_ids?: string; set_ids?: string }) => {
    const { data } = await api.get('/api/trading/compare', { params })
    return data
  },
  getMarketBreadth: async () => api.get('/api/trading/analytics/market-breadth').then(r => r.data),
  getPriceDistribution: async () => api.get('/api/trading/analytics/price-distribution').then(r => r.data),
  getVolatility: async () => api.get('/api/trading/analytics/volatility').then(r => r.data),
  getCorrelationMatrix: async (type = 'cards', limit = 10) => api.get('/api/trading/analytics/correlation-matrix', { params: { type, limit } }).then(r => r.data),
  getVolumeProfile: async (cardId: string) => api.get(`/api/trading/analytics/volume-profile/${cardId}`).then(r => r.data),
  getMarketSentiment: async () => api.get('/api/trading/analytics/market-sentiment').then(r => r.data),
  getInventoryWaterfall: async (days = 30) => api.get('/api/trading/analytics/inventory-waterfall', { params: { days } }).then(r => r.data),
  getTopByMetric: async (params: Record<string, any>) => api.get('/api/trading/analytics/top-by-metric', { params }).then(r => r.data),
  getSetComparison: async (setIds: string) => api.get('/api/trading/analytics/set-comparison', { params: { set_ids: setIds } }).then(r => r.data),
  getDepthChart: async (cardId: string) => api.get(`/api/trading/analytics/depth-chart/${cardId}`).then(r => r.data),
}

// ---------------------------------------------------------------------------
// Admin Card Manager
// ---------------------------------------------------------------------------

import type {
  AdminCardListParams,
  AdminCardListResponse,
  AdminCardDetail,
  MergePreflightSummary,
  CardSyncReport,
  SyncOptions,
  AdminBlueprintMapping,
  AdminSetsStatsResponse,
  PerSetDiffResponse,
  PerSetSyncApplyBody,
  PerSetSyncApplyResponse,
  PerSetSyncHistoryResponse,
  PerSetParityResponse,
  SealedSyncForSetResponse,
  RetireJsonResponse,
} from '@/types/admin'

export const adminCardsApi = {
  list: async (params: AdminCardListParams = {}): Promise<AdminCardListResponse> => {
    const { data } = await api.get<AdminCardListResponse>('/api/admin/cards', { params })
    return data
  },
  get: async (id: string): Promise<AdminCardDetail> => {
    const { data } = await api.get<AdminCardDetail>(`/api/admin/cards/${encodeURIComponent(id)}`)
    return data
  },
  update: async (id: string, fields: Record<string, any>): Promise<{ card: any; changed: string[] }> => {
    const { data } = await api.patch(`/api/admin/cards/${encodeURIComponent(id)}`, fields)
    return data
  },
  linkBlueprint: async (id: string, blueprint_id: number): Promise<{ ok: true }> => {
    const { data } = await api.post(`/api/admin/cards/${encodeURIComponent(id)}/link-blueprint`, { blueprint_id })
    return data
  },
  unlinkBlueprint: async (id: string, blueprint_id: number): Promise<{ ok: true; deleted: number }> => {
    const { data } = await api.delete(
      `/api/admin/cards/${encodeURIComponent(id)}/blueprint/${blueprint_id}`
    )
    return data
  },
  setCardmarketUrl: async (id: string, url: string | null): Promise<{ ok: true; cardmarket_url: string | null }> => {
    const { data } = await api.put(`/api/admin/cards/${encodeURIComponent(id)}/cardmarket-url`, { url })
    return data
  },
  /** POST /api/admin/cards/:id/image/redownload — re-fetch the linked CT blueprint image into the local cache, extends the manual-edit mask with `image_url`. */
  redownloadImage: async (
    id: string
  ): Promise<{ image_url: string; cached_path: string; bytes: number }> => {
    const { data } = await api.post(
      `/api/admin/cards/${encodeURIComponent(id)}/image/redownload`
    )
    return data
  },
  /** POST /api/admin/cards/:id/image/download-from-url — cache an arbitrary `https://` (or local `/uploads`/`/cached-images/`) URL as the card's image. */
  downloadImageFromUrl: async (
    id: string,
    url: string
  ): Promise<{ image_url: string; cached_path: string; bytes: number }> => {
    const { data } = await api.post(
      `/api/admin/cards/${encodeURIComponent(id)}/image/download-from-url`,
      { url }
    )
    return data
  },
  mergePreflight: async (from: string, to: string): Promise<MergePreflightSummary> => {
    const { data } = await api.post<MergePreflightSummary>('/api/admin/cards/merge/preflight', { from, to })
    return data
  },
  merge: async (from: string, to: string): Promise<{ ok: true }> => {
    const { data } = await api.post('/api/admin/cards/merge', { from, to, confirm: true })
    return data
  },
  runSync: async (opts: Partial<SyncOptions>): Promise<{ report: CardSyncReport }> => {
    const { data } = await api.post<{ report: CardSyncReport }>('/api/admin/cards/sync', opts)
    return data
  },
  getSyncReport: async (batchId: string): Promise<{ report: CardSyncReport }> => {
    const { data } = await api.get<{ report: CardSyncReport }>(`/api/admin/cards/sync/${batchId}`)
    return data
  },
  syncHistory: async (limit = 20) => {
    const { data } = await api.get('/api/admin/cards/sync/history', { params: { limit } })
    return data as {
      entries: Array<{
        audit_id: number
        action: string
        batchId: string | null
        created_at: string
        totals: any
        card_count?: number
      }>
    }
  },
  syncCompare: async (a: string, b: string) => {
    const { data } = await api.get('/api/admin/cards/sync/compare', { params: { a, b } })
    return data as {
      a: { batchId: string; totals: any; startedAt: string }
      b: { batchId: string; totals: any; startedAt: string }
      only_in_a: Array<{ card_id: string; field: string; before: any; after: any }>
      only_in_b: Array<{ card_id: string; field: string; before: any; after: any }>
      both: Array<{ card_id: string; field: string; a_after: any; b_after: any; same: boolean }>
      drift: Array<{ card_id: string; field: string; a_after: any; b_after: any; same: boolean }>
    }
  },
  searchBlueprints: async (params: { q?: string; expansion_id?: number; limit?: number; offset?: number }): Promise<{ blueprints: AdminBlueprintMapping[] }> => {
    const { data } = await api.get<{ blueprints: AdminBlueprintMapping[] }>(
      '/api/admin/cards/blueprints/search',
      { params }
    )
    return data
  },

  // ---------------- Stage 2: missing-set helper ----------------
  getMissingSets: async (
    batchId: string
  ): Promise<{
    batchId: string
    missingSets: Array<{ set_id: string; count: number; samples: string[]; blueprint_ids: number[] }>
  }> => {
    const { data } = await api.get(
      `/api/admin/cards/sync/${encodeURIComponent(batchId)}/missing-sets`
    )
    return data
  },
  createSet: async (payload: {
    id: string
    name: string
    code?: string
    release_date?: string
    total_cards?: number
    /** Pass true to bypass the Levenshtein near-duplicate guardrail. */
    force?: boolean
  }): Promise<{ ok: true; id: string; name: string }> => {
    const { data } = await api.post('/api/admin/cards/sets', payload)
    return data
  },
  updateSet: async (
    id: string,
    payload: {
      name?: string
      code?: string
      release_date?: string | null
      total_cards?: number | null
      set_type?: string | null
      rotation_date?: string | null
    }
  ): Promise<{ ok: true; set: any }> => {
    const { data } = await api.patch(`/api/admin/cards/sets/${encodeURIComponent(id)}`, payload)
    return data
  },
  deleteSet: async (id: string): Promise<{ ok: true; id: string }> => {
    const { data } = await api.delete(`/api/admin/cards/sets/${encodeURIComponent(id)}`)
    return data
  },
  listSetsWithStats: async (
    params: {
      q?: string
      sort?: 'id' | 'name' | 'release_date' | 'total_cards' | 'last_ct_sync_at'
      dir?: 'asc' | 'desc'
      stale_days?: number
    } = {}
  ): Promise<AdminSetsStatsResponse> => {
    const { data } = await api.get<AdminSetsStatsResponse>(
      '/api/admin/cards/sets/stats',
      { params }
    )
    return data
  },

  // ---------------- Stage 3: Unmapped workbench ----------------
  listUnmappedBlueprints: async (params: {
    expansion_id?: number
    q?: string
    limit?: number
    offset?: number
    min_confidence?: number
  } = {}) => {
    const { data } = await api.get('/api/admin/cards/blueprints/unmapped', { params })
    return data as { blueprints: any[]; total: number }
  },
  bulkConfirmBlueprints: async (links: Array<{ blueprint_id: number; card_id: string }>) => {
    const { data } = await api.post('/api/admin/cards/blueprints/bulk-confirm', { links })
    return data as {
      ok: true
      batchId: string
      results: Array<{ blueprint_id: number; card_id: string; ok: boolean; error?: string }>
    }
  },

  // ---------------- Stage 4: Selective sync apply ----------------
  applySelectiveSync: async (payload: {
    batchId: string
    selections: Array<{ card_id: string; fields: Record<string, any>; blueprint_id?: number | null }>
    createIds?: string[]
  }) => {
    const { data } = await api.post('/api/admin/cards/sync/apply-selective', payload)
    return data as {
      ok: true
      batchId: string
      results: Array<{
        card_id: string
        ok: boolean
        written?: string[]
        skipped?: Array<{ field: string; reason: string }>
        error?: string
      }>
      created: string[]
    }
  },

  // ---------------- Stage 5: Conflict resolver ----------------
  getBlueprintCandidates: async (cardId: string) => {
    const { data } = await api.get(`/api/admin/cards/${encodeURIComponent(cardId)}/blueprint-candidates`)
    return data as { card: any; candidates: any[] }
  },
  resolveConflict: async (
    cardId: string,
    payload: {
      canonical_blueprint_id: number
      drop_blueprint_ids?: number[]
      protect_fields?: string[]
    }
  ) => {
    const { data } = await api.post(
      `/api/admin/cards/${encodeURIComponent(cardId)}/resolve-conflict`,
      payload
    )
    return data as { ok: true; card_id: string; canonical_blueprint_id: number }
  },
  bulkResolveConflicts: async (
    items: Array<{
      card_id: string
      canonical_blueprint_id: number
      drop_blueprint_ids?: number[]
      protect_fields?: string[]
    }>,
    preset?: string
  ) => {
    const { data } = await api.post('/api/admin/cards/conflicts/bulk-resolve', { items, preset })
    return data as {
      ok: true
      batchId: string
      results: Array<{ card_id: string; ok: boolean; error?: string }>
    }
  },

  // ---------------- Stage 7: CM URL fixer ----------------
  bulkSuggestCardmarketUrls: async (params: {
    card_ids?: string[]
    set_id?: string
    only_missing?: boolean
  }) => {
    const { data } = await api.post('/api/admin/cards/cardmarket-urls/bulk-suggest', params)
    return data as {
      count: number
      suggestions: Array<{
        card_id: string
        set_id: string
        name: string | null
        suggested_url: string | null
      }>
    }
  },
  validateCardmarketUrls: async (params: {
    card_ids?: string[]
    set_id?: string
    concurrency?: number
  }) => {
    const { data } = await api.post('/api/admin/cards/cardmarket-urls/validate', params)
    return data as {
      ok: true
      checked: number
      results: Array<{ card_id: string; url: string; status_code: number | null; error: string | null }>
      summary: { ok: number; broken: number; errored: number }
    }
  },
  listCardmarketUrlValidations: async (params: {
    status?:
      | 'ok'
      | 'broken'
      | 'errored'
      | '2xx'
      | '3xx'
      | '4xx'
      | '5xx'
      | 'timeout'
      | 'never_validated'
    limit?: number
    offset?: number
    stale_days?: number
  } = {}) => {
    const { data } = await api.get('/api/admin/cards/cardmarket-urls/validations', { params })
    return data as { validations: any[]; total: number }
  },
  cardmarketUrlValidationsStats: async () => {
    const { data } = await api.get('/api/admin/cards/cardmarket-urls/validations/stats')
    return data as {
      total: number
      by_status: {
        '2xx': number
        '3xx': number
        '4xx': number
        '5xx': number
        timeout: number
        never_validated: number
      }
      stale_count: number
    }
  },
  suggestCardmarketUrl: async (cardId: string) => {
    const { data } = await api.post(
      `/api/admin/cards/${encodeURIComponent(cardId)}/cardmarket-url/suggest`
    )
    return data as { card_id: string; url: string | null }
  },

  // ---------------- Stage 6: Bulk operations + suggestions + audit ----------------
  bulkEdit: async (card_ids: string[], fields: Record<string, any>) => {
    const { data } = await api.post('/api/admin/cards/bulk-edit', { card_ids, fields })
    return data as {
      ok: true
      batchId: string
      results: Array<{ card_id: string; ok: boolean; changed?: string[]; error?: string }>
    }
  },
  bulkMarkManual: async (card_ids: string[], fields: string[]) => {
    const { data } = await api.post('/api/admin/cards/bulk-mark-manual', { card_ids, fields })
    return data as {
      ok: true
      batchId: string
      results: Array<{ card_id: string; ok: boolean; mask?: string[]; error?: string }>
    }
  },
  bulkLinkBlueprint: async (links: Array<{ card_id: string; blueprint_id: number }>) => {
    const { data } = await api.post('/api/admin/cards/bulk-link-blueprint', { links })
    return data as {
      ok: true
      batchId: string
      results: Array<{ card_id: string; blueprint_id: number; ok: boolean; error?: string }>
    }
  },
  bulkSetCardmarketUrl: async (updates: Array<{ card_id: string; url: string | null }>) => {
    const { data } = await api.post('/api/admin/cards/bulk-cardmarket-url', { updates })
    return data as {
      ok: true
      batchId: string
      results: Array<{ card_id: string; ok: boolean; error?: string }>
    }
  },
  bulkMerge: async (pairs: Array<{ from: string; to: string }>) => {
    const { data } = await api.post('/api/admin/cards/bulk-merge', { pairs })
    return data as {
      ok: true
      batchId: string
      results: Array<{ from: string; to: string; ok: boolean; error?: string }>
    }
  },
  blueprintSuggestionsForCard: async (cardId: string, limit = 3) => {
    const { data } = await api.get(
      `/api/admin/cards/${encodeURIComponent(cardId)}/blueprint-suggestions`,
      { params: { limit } }
    )
    return data as {
      card_id: string
      suggestions: Array<{ blueprint_id: number; confidence: number; method: string; mapping?: any }>
    }
  },
  cardAudit: async (cardId: string, params: { limit?: number; offset?: number; action?: string } = {}) => {
    const { data } = await api.get(`/api/admin/cards/${encodeURIComponent(cardId)}/audit`, { params })
    return data as { entries: any[]; total: number }
  },
  undoAudit: async (auditId: number) => {
    const { data } = await api.post(`/api/admin/cards/audit/${auditId}/undo`)
    return data as
      | { ok: true; auditId: number; undoSnapshot: any }
      | { irreversible: true; reason: string; details: string }
  },

  // ---------------- Phase 3: Dedup workbench ----------------
  /** POST /api/admin/cards/dedup/scan — find groups of cards sharing `(set_id, number, name, art_type)` (convention-violation duplicates only). */
  dedupScan: async (
    params: { set_id?: string; limit?: number } = {}
  ): Promise<{
    candidates: Array<{
      set_id: string
      number: string
      name: string
      art_type: string | null
      count: number
      cards: Array<{
        id: string
        set_id: string
        number: string
        name: string
        art_type: string | null
        foil_variant: string | null
        is_errata: number | null
        errata_of: string | null
        rarity: string | null
        image_url: string | null
        last_ct_sync_at: string | null
      }>
    }>
    total: number
  }> => {
    const { data } = await api.post('/api/admin/cards/dedup/scan', params)
    return data
  },
  /** POST /api/admin/cards/dedup/merge — collapse `loser_id` into `keeper_id`; reuses canonical merge sequence and rewrites audit history. Irreversible. */
  dedupMerge: async (keeper_id: string, loser_id: string): Promise<{
    ok: true
    batchId: string
    keeper_id: string
    loser_id: string
  }> => {
    const { data } = await api.post('/api/admin/cards/dedup/merge', { keeper_id, loser_id })
    return data
  },

  // ---------------- Phase 3: Errata mark/clear ----------------
  /** POST /api/admin/cards/:id/mark-errata — flag this row as the post-errata variant of `errata_of`; also extends the manual-edit mask so sync can't clear the flags. */
  markErrata: async (
    cardId: string,
    payload: {
      errata_of: string
      errata_reason?: string
      errata_published_at?: string | null
    }
  ): Promise<{ ok: true; card: any }> => {
    const { data } = await api.post(
      `/api/admin/cards/${encodeURIComponent(cardId)}/mark-errata`,
      payload
    )
    return data
  },
  /** POST /api/admin/cards/:id/clear-errata — reset `is_errata`/`errata_*` columns; mask entries are NOT cleared automatically. */
  clearErrata: async (cardId: string): Promise<{ ok: true; card: any }> => {
    const { data } = await api.post(
      `/api/admin/cards/${encodeURIComponent(cardId)}/clear-errata`
    )
    return data
  },

  // Sets PATCH was widened in Phase 2 to include set_type / rotation_date.
  // Re-export updateSet through this widened type. (The original `updateSet`
  // above uses an inline payload type — keep this method for the new fields.)
  updateSetExtended: async (
    id: string,
    payload: {
      name?: string
      code?: string
      release_date?: string | null
      total_cards?: number | null
      set_type?: string | null
      rotation_date?: string | null
    }
  ): Promise<{ ok: true; set: any }> => {
    const { data } = await api.patch(`/api/admin/cards/sets/${encodeURIComponent(id)}`, payload)
    return data
  },

  // ---------------- Phase 4b: per-set sync workflow ----------------
  /** GET /api/admin/cards/sync/by-set/:setId/diff — dry-run diff envelope. */
  getSyncDiff: async (setId: string): Promise<PerSetDiffResponse> => {
    const { data } = await api.get<PerSetDiffResponse>(
      `/api/admin/cards/sync/by-set/${encodeURIComponent(setId)}/diff`
    )
    return data
  },
  /** POST /api/admin/cards/sync/by-set/:setId/apply — transactional apply. */
  applySyncDiff: async (
    setId: string,
    body: PerSetSyncApplyBody = {}
  ): Promise<PerSetSyncApplyResponse> => {
    const { data } = await api.post<PerSetSyncApplyResponse>(
      `/api/admin/cards/sync/by-set/${encodeURIComponent(setId)}/apply`,
      body
    )
    return data
  },
  /** GET /api/admin/cards/sync/by-set/:setId/history — last 25 audit rows. */
  getSyncHistoryForSet: async (setId: string): Promise<PerSetSyncHistoryResponse> => {
    const { data } = await api.get<PerSetSyncHistoryResponse>(
      `/api/admin/cards/sync/by-set/${encodeURIComponent(setId)}/history`
    )
    return data
  },
  /** GET /api/admin/cards/parity/:setId — parity counts + recommendation. */
  getParity: async (setId: string): Promise<PerSetParityResponse> => {
    const { data } = await api.get<PerSetParityResponse>(
      `/api/admin/cards/parity/${encodeURIComponent(setId)}`
    )
    return data
  },
  /** POST /api/admin/cards/sealed/sync/:setId — sealed-only convenience. */
  syncSealedForSet: async (setId: string): Promise<SealedSyncForSetResponse> => {
    const { data } = await api.post<SealedSyncForSetResponse>(
      `/api/admin/cards/sealed/sync/${encodeURIComponent(setId)}`
    )
    return data
  },
  /**
   * POST /api/admin/cards/sets/:setId/retire-json — flip seed_source to
   * 'cardtrader'. 409 when parity isn't green unless force=true.
   */
  retireJsonForSet: async (setId: string, force = false): Promise<RetireJsonResponse> => {
    const { data } = await api.post<RetireJsonResponse>(
      `/api/admin/cards/sets/${encodeURIComponent(setId)}/retire-json`,
      { force }
    )
    return data
  },
}

/**
 * Public-facing errata pair fetch (auth-gated). Returns the linked errata pair
 * for a card so the UI can render a side-by-side diff between corrected and
 * errata variants. Used by AdminCardEditModal and the collector-facing
 * CardDetailModal.
 */
export const errataApi = {
  getPair: async (
    cardId: string
  ): Promise<{
    card: any | null
    corrected: any | null
    errata: any | null
  }> => {
    const { data } = await api.get(`/api/cards/${encodeURIComponent(cardId)}/errata-pair`)
    return data
  },
}
